import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ReusableTable from "../../../components/ReusableTable/ReusableTable";
import { CheckStatusChip } from "../../../components/CheckStatusChip/CheckStatusChip";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import InformationStepsCard from "../../../components/InformationStepsCard/InformationStepsCard";
import FilterBar from "../../../components/FilterNSearchBar/FilterNSearchBar";
import ScopeActionButton from "../../../components/ScopeActionButton/ScopeActionButton";
import { statusFilterOptions } from "../../../utils/enums";
import {
  GET_BRANCHES,
  GET_CODE_SECURITY_SUMMARY,
  GET_REPOS,
  SET_REPO_SCOPE,
  SET_BRANCHES_SCOPE,
} from "../../../apis/organization-apis/CodeSecurity-apis/CodeSecurityApis";
import {
  SCOPE_TOGGLE_SX,
  TABLE_MIN_WIDTH_SX,
  FILTER_BAR_WRAP_SX,
  CARD_HEADER_SX,
  matchesFilter,
  includesQuery,
  countLabel,
  outlinedButtonSx,
  SECRETS_FOUND_FILTER_OPTIONS,
  secretsFoundValue,
  hasActiveFilters,
  useCodeSecurityListFilters,
  CODE_SECURITY_PATH,
  compareStatusFailingFirst,
} from "./codeSecurityShared";

const levelColor = (level) => {
  if (level === "confirmed") return "error";
  if (level === "unconfirmed") return "warning";
  return "default";
};

const CodeSecurityBranches = () => {
  const { repoId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [snackbar, setSnackbar] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const {
    tab,
    searchQuery,
    tableFilters,
    setTab,
    setSearchQuery,
    setTableFilters,
    clearFilters,
  } = useCodeSecurityListFilters();
  const inScope = tab === "in";

  const { data, loading, refetch } = useQuery(GET_BRANCHES, {
    variables: { repo_data_id: repoId },
    skip: !repoId,
    fetchPolicy: "network-only",
  });
  const { data: reposData, refetch: refetchRepos } = useQuery(GET_REPOS, {
    fetchPolicy: "network-only",
  });
  const { data: summaryData } = useQuery(GET_CODE_SECURITY_SUMMARY, {
    fetchPolicy: "network-only",
  });
  const scoredLevels =
    summaryData?.getCodeSecuritySummary?.data?.scored_levels || ["confirmed"];

  const allBranches = data?.getBranches?.data || [];
  const scopedBranches = useMemo(
    () =>
      allBranches.filter((row) =>
        inScope ? row.in_scope === true : row.in_scope !== true
      ),
    [allBranches, inScope]
  );
  const repo = (reposData?.getRepos?.data || []).find(
    (row) => String(row.id) === String(repoId)
  );
  const heading = repo
    ? `${repo.owner}/${repo.repo}`
    : allBranches[0]
      ? `${allBranches[0].owner}/${allBranches[0].repo}`
      : "Repository";
  const repoInScope = repo?.in_scope === true;

  const [setRepoScope, { loading: repoScopeLoading }] =
    useMutation(SET_REPO_SCOPE);
  const [setBranchesScope, { loading: bulkScopeLoading }] =
    useMutation(SET_BRANCHES_SCOPE);

  const filteredBranches = useMemo(
    () =>
      scopedBranches
        .filter(
          (row) =>
            includesQuery(searchQuery, row.branch) &&
            matchesFilter(tableFilters.status, row.status) &&
            matchesFilter(
              tableFilters.findings,
              secretsFoundValue(row.level_counts, scoredLevels)
            )
        )
        .sort((a, b) => {
          const byStatus = compareStatusFailingFirst(a, b);
          if (byStatus !== 0) return byStatus;
          if (Boolean(a.is_default_branch) !== Boolean(b.is_default_branch)) {
            return a.is_default_branch ? -1 : 1;
          }
          return String(a.branch || "").localeCompare(String(b.branch || ""));
        }),
    [scopedBranches, searchQuery, tableFilters, scoredLevels]
  );

  const selectedBranches = useMemo(
    () => filteredBranches.filter((row) => selectedIds.has(String(row.id))),
    [filteredBranches, selectedIds]
  );
  const canAddSelected =
    repoInScope && selectedBranches.some((row) => row.in_scope !== true);
  const canRemoveSelected = selectedBranches.some(
    (row) => row.in_scope === true && !row.is_default_branch
  );

  const columns = useMemo(
    () => [
      { label: "", field: "select", width: "5%", align: "center" },
      { label: "Branch", field: "branch", width: "33%", align: "left" },
      {
        label: "Secrets found",
        field: "findings",
        width: "26%",
        align: "left",
        filterConfig: { filterOptions: SECRETS_FOUND_FILTER_OPTIONS },
      },
      {
        label: "Status",
        field: "status",
        width: "22%",
        align: "left",
        filterConfig: { filterOptions: statusFilterOptions },
      },
      { label: "Actions", field: "action", width: "14%", align: "center" },
    ],
    []
  );

  const handleSelectRow = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    const allVisibleSelected =
      filteredBranches.length > 0 &&
      filteredBranches.every((row) => selectedIds.has(String(row.id)));
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredBranches.map((row) => String(row.id))));
  };

  const handleTabChange = (_, value) => {
    if (value === null) return;
    setTab(value);
    setSelectedIds(new Set());
  };

  const handleRepoScope = async (next) => {
    const result = await setRepoScope({
      variables: { repo_data_id: repoId, in_scope: next },
    });
    const payload = result?.data?.setRepoScope;
    setSnackbar({
      message: payload?.message || "Updated",
      variant: payload?.success ? "success" : "error",
    });
    setSelectedIds(new Set());
    await Promise.all([refetch(), refetchRepos()]);
  };

  const handleBulkScope = async (next) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const result = await setBranchesScope({
      variables: { branch_data_ids: ids, in_scope: next },
    });
    const payload = result?.data?.setBranchesScope;
    setSnackbar({
      message: payload?.message || "Updated",
      variant: payload?.success ? "success" : "error",
    });
    if (payload?.success) setSelectedIds(new Set());
    await refetch();
  };

  const renderHeaderCell = (column) => {
    if (column?.field !== "select") return null;
    const allSelected =
      filteredBranches.length > 0 &&
      filteredBranches.every((row) => selectedIds.has(String(row.id)));
    const someSelected =
      !allSelected &&
      filteredBranches.some((row) => selectedIds.has(String(row.id)));
    return (
      <Checkbox
        size="small"
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        sx={{ p: 0 }}
      />
    );
  };

  const renderCell = (row, column) => {
    switch (column.field) {
      case "select":
        return (
          <Checkbox
            size="small"
            checked={selectedIds.has(String(row.id))}
            onChange={() => handleSelectRow(row.id)}
            onClick={(event) => event.stopPropagation()}
            sx={{ p: 0 }}
          />
        );
      case "branch":
        return (
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
            <Typography variant="body2" fontWeight={600}>
              {row.branch}
            </Typography>
            {row.is_default_branch && (
              <Chip
                size="small"
                label="Default"
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  backgroundColor: theme.customColors.status.info.bg,
                  color: theme.customColors.status.info.text,
                  border: "none",
                }}
              />
            )}
          </Box>
        );
      case "findings": {
        const visibleCounts = (row.level_counts || []).filter((lc) =>
          scoredLevels.includes(lc.level)
        );
        return visibleCounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        ) : (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {visibleCounts.map((lc) => (
              <Chip
                key={lc.level}
                size="small"
                variant="outlined"
                color={levelColor(lc.level)}
                label={`${
                  lc.level === "confirmed" ? "Confirmed" : "Possible"
                }: ${lc.count}`}
              />
            ))}
          </Box>
        );
      }
      case "status":
        return <CheckStatusChip status={row.status} />;
      case "action":
        return (
          <Tooltip title="View secrets" arrow>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`${CODE_SECURITY_PATH}/${repoId}/${row.id}`);
              }}
              sx={{
                color: theme.customColors.icons.dark,
                "&:hover": {
                  backgroundColor: theme.customColors.background.light,
                  transform: "translateX(2px)",
                  transition: "all 0.2s ease",
                },
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        );
      default:
        return row[column.field];
    }
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1.5 }}>
        <Link
          component={RouterLink}
          to={CODE_SECURITY_PATH}
          underline="hover"
          color="text.secondary"
        >
          Code Security
        </Link>
        <Typography color="text.primary">{heading}</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 1.5 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="These are the branches in this repository. Open one to see findings for that branch."
          steps={[
            "The **default branch is always in scope** while this repository is in scope. You cannot remove it.",
            "Use **In Scope** for the branches we should scan. Everything else stays on **Out of Scope** and is ignored.",
            "Select branches and use **Add to scans** or **Remove from scans** in the table header to change several at once.",
            "Use **Remove from scans** at the bottom to stop monitoring this whole repository. Existing findings are kept.",
          ]}
          footerText="💡 A scan runs across every in-scope repository at once — not branch by branch."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          border: "1px solid #E8EAED",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                Branches
              </Typography>
              <ToggleButtonGroup
                value={tab}
                exclusive
                onChange={handleTabChange}
                aria-label="branch scope"
                size="small"
                sx={SCOPE_TOGGLE_SX}
              >
                <ToggleButton value="in">In Scope</ToggleButton>
                <ToggleButton value="out">Out of Scope</ToggleButton>
              </ToggleButtonGroup>
              {hasActiveFilters(searchQuery, tableFilters) && (
                <Chip
                  label="Filtered"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.customColors.text.secondary,
                    borderColor: theme.customColors.border.main,
                    background: theme.customColors.background.light,
                  }}
                />
              )}
            </Box>
          }
          action={
            selectedIds.size > 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ScopeActionButton
                  mode={inScope ? "out" : "in"}
                  label={inScope ? "Remove from scans" : "Add to scans"}
                  disabled={
                    inScope
                      ? !canRemoveSelected || bulkScopeLoading
                      : !canAddSelected || bulkScopeLoading
                  }
                  disabledReason={
                    inScope
                      ? !canRemoveSelected
                        ? "The default branch is always monitored"
                        : ""
                      : !repoInScope
                        ? "Add the repository to scans first"
                        : !canAddSelected
                          ? "Selected branches are already monitored"
                          : ""
                  }
                  onClick={() => handleBulkScope(!inScope)}
                />
                <Button
                  variant="outlined"
                  onClick={() => setSelectedIds(new Set())}
                  sx={outlinedButtonSx(theme)}
                >
                  Clear
                </Button>
              </Box>
            ) : null
          }
          sx={CARD_HEADER_SX}
        />
        <CardContent sx={{ p: 0 }}>
          <Box sx={FILTER_BAR_WRAP_SX}>
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={(event) => setSearchQuery(event.target.value)}
              filters={[]}
              selectedFilter={null}
              onFilterChange={() => {}}
              onClearFilters={clearFilters}
              countLabel={countLabel(filteredBranches.length, "branch")}
            />
          </Box>
          <Divider sx={{ borderColor: "#E8EAED" }} />
          <Box sx={TABLE_MIN_WIDTH_SX}>
            <ReusableTable
              columns={columns}
              data={filteredBranches}
              loading={loading}
              renderCell={renderCell}
              renderHeaderCell={renderHeaderCell}
              onFilterChange={setTableFilters}
              filters={tableFilters}
              height="auto"
              emptyState={
                <Typography variant="body1" color="text.secondary">
                  {scopedBranches.length === 0
                    ? inScope
                      ? "Nothing is being monitored yet. Open Out of Scope and pick a branch."
                      : "No branches out of scope."
                    : "No branches match your search."}
                </Typography>
              }
            />
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
          mt: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.customColors.text.disabled,
            fontStyle: "italic",
            fontSize: "12px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {repoInScope
            ? "Remove this repository from scans to stop monitoring it. Existing findings are kept."
            : "Add this repository to scans. We will include the default branch."}
        </Typography>
        {repoInScope ? (
          <Button
            variant="outlined"
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={() => handleRepoScope(false)}
            disabled={repoScopeLoading}
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              borderColor: theme.customColors.status.error.main,
              color: theme.customColors.status.error.main,
              borderWidth: "2px",
              flexShrink: 0,
              "&:hover": {
                borderColor: theme.customColors.status.error.text,
                backgroundColor: theme.customColors.status.error.bg,
                color: theme.customColors.status.error.text,
              },
            }}
          >
            {repoScopeLoading ? "Removing..." : "Remove from scans"}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleRepoScope(true)}
            disabled={repoScopeLoading}
            sx={{ flexShrink: 0 }}
          >
            {repoScopeLoading ? "Adding..." : "Add to scans"}
          </Button>
        )}
      </Box>

      {snackbar && (
        <ConfirmSnackbar
          snackbarInfo={snackbar}
          setSnackbarInfo={setSnackbar}
        />
      )}
    </Box>
  );
};

export default CodeSecurityBranches;
